/**
 * PreorderCreateModal — Création de précommandes pains depuis le POS
 *
 * UNE page plein écran (30/10/2026) : client à gauche, tous les produits au
 * centre, commande + date de retrait + validation à droite. Avant : une fenêtre
 * d'un tiers d'écran en trois étapes, des cartes produit sur deux colonnes —
 * il fallait faire défiler 33 articles et revenir en arrière pour corriger
 * (« obligation de scroller et recherche fastidieuse », Philippe).
 * Clavier à l'écran : la page est un « champ de page » (`data-clavier="page"`),
 * elle se resserre à côté du clavier et passe sur une colonne (requêtes de
 * conteneur) au lieu de passer dessous.
 * Indépendant du panier POS (pas de paiement à la création)
 * Impression ticket EPSON via Hardware Bridge
 */
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { logger } from '../../utils/logger';
import { PrinterAdapterFactory } from '../../services/printers';
import type { PrinterConfig, PrintJob, PrintLine } from '../../services/printers';
import CustomerNameAutocomplete from '../preorders/CustomerNameAutocomplete';
import { noteLibre } from '../../utils/notesPrecommande';

interface PreorderProduct {
  id: string;
  name: string;
  unit?: string;
  preorder_type_id?: string | null;
  prices: Array<{ amount_cents: number }>;
}

export interface SelectedItem {
  product_id: string;
  product_name: string;
  quantity: number;
  unit: string;
  price_cents: number;
  sliced: boolean;
}

/** Précommande déjà en cours pour ce client (GET /preorders/customer-open). */
interface PrecommandeEnCours {
  id: string;
  preorder_number: string;
  customer_name: string;
  pickup_date: string;
  status: string;
  source: string;
  payment_status?: string | null;
  items: Array<{ product_name: string; quantity: number; unit?: string | null; prep_options?: string[] }>;
}

interface PreorderCreateModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Pré-remplissage pour « Reconduire » une précommande passée : client + lignes + notes.
   * La DATE n'est volontairement PAS reprise (on choisit un nouveau retrait au montage).
   */
  initialData?: {
    customerName?: string;
    customerPhone?: string;
    items?: SelectedItem[];
    notes?: string;
    pickupDate?: string;
  };
  /** Si défini, le modal MODIFIE cette précommande (PATCH /:id/modify) au lieu d'en créer une. */
  editPreorderId?: string;
}

// Génère les prochaines dates de retrait selon les jours autorisés (0=dim..6=sam).
// Data-driven : l'union des pickup_days des types de précommande (fallback ven/sam).
/**
 * Dates de retrait proposées.
 *
 * 6 dates sur 28 jours, soit trois semaines avec deux cuissons par semaine : un
 * client qui réserve son pain pour les vacances, ou pour un événement, n'avait
 * aucune date à choisir. On ouvre à 16 dates sur 12 semaines — la contrainte
 * réelle n'est pas l'horizon, c'est ce que le boulanger accepte de bloquer.
 *
 * Les congés du producteur restent traités par le serveur, qui reporte au
 * prochain jour de cuisson et l'annonce à l'écran.
 */
/** « vendredi, samedi » — pour dire au vendeur ce que la commande autorise. */
function formatJours(days: number[]): string {
  const noms = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const tries = days.slice().sort((a, b) => a - b).map((d) => noms[d]).filter(Boolean);
  return tries.length ? tries.join(', ') : 'aucun jour configure';
}

/** Une date ISO (AAAA-MM-JJ) au format des boutons. */
function dateProposee(iso: string): { label: string; value: string } {
  const d = new Date(iso + 'T12:00:00');
  return { label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }), value: iso };
}

function buildPickupDates(allowedWeekdays: number[], count = 16): { label: string; value: string }[] {
  const days = allowedWeekdays.length ? allowedWeekdays : [5, 6];
  const res: { label: string; value: string }[] = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1); // à partir de demain
  for (let i = 0; i < 84 && res.length < count; i++) {
    if (days.includes(d.getDay())) {
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      res.push({
        label: d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }),
        value,
      });
    }
    d.setDate(d.getDate() + 1);
  }
  return res;
}

/** Normalise les accents pour l'impression EPSON (encodage PC858) */
export function normalizeForPrint(text: string): string {
  const accentsMap: Record<string, string> = {
    'à': 'a', 'á': 'a', 'â': 'a', 'ã': 'a', 'ä': 'a',
    'è': 'e', 'é': 'e', 'ê': 'e', 'ë': 'e',
    'ì': 'i', 'í': 'i', 'î': 'i', 'ï': 'i',
    'ò': 'o', 'ó': 'o', 'ô': 'o', 'õ': 'o', 'ö': 'o',
    'ù': 'u', 'ú': 'u', 'û': 'u', 'ü': 'u',
    'ç': 'c', 'ñ': 'n',
    'À': 'A', 'Á': 'A', 'Â': 'A', 'Ã': 'A', 'Ä': 'A',
    'È': 'E', 'É': 'E', 'Ê': 'E', 'Ë': 'E',
    'Ì': 'I', 'Í': 'I', 'Î': 'I', 'Ï': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ô': 'O', 'Õ': 'O', 'Ö': 'O',
    'Ù': 'U', 'Ú': 'U', 'Û': 'U', 'Ü': 'U',
    'Ç': 'C', 'Ñ': 'N',
    // Ligatures : absentes du jeu PC858, le bridge imprimait « b?uf » (23/10/2026).
    'œ': 'oe', 'Œ': 'OE', 'æ': 'ae', 'Æ': 'AE',
    '€': 'EUR',
  };
  return text.split('').map(char => accentsMap[char] || char).join('');
}

/** Génère un PrintJob pour ticket EPSON de précommande (ou de commande client) */
export function generatePreorderReceipt(data: {
  /** Titre du ticket : « PRECOMMANDE » par défaut, « COMMANDE » pour l'épicerie. */
  titre?: string;
  preorderNumber: string;
  customerName: string;
  customerPhone?: string;
  pickupDate: string;
  items: SelectedItem[];
  notes?: string;
  totalCents: number;
  storeName: string;
}): PrintJob {
  const lines: PrintLine[] = [];

  // Header magasin
  lines.push({ type: 'text', content: normalizeForPrint(data.storeName), align: 'center', bold: true });
  lines.push({ type: 'separator' });

  // Titre PRECOMMANDE
  lines.push({ type: 'text', content: data.titre || 'PRECOMMANDE', align: 'center', bold: true, fontSize: 'large' });
  lines.push({ type: 'text', content: data.preorderNumber, align: 'center', bold: true, fontSize: 'large' });
  lines.push({ type: 'separator' });

  // Client
  lines.push({ type: 'text', content: normalizeForPrint(`Client : ${data.customerName}`) });
  if (data.customerPhone) {
    lines.push({ type: 'text', content: `Tel : ${data.customerPhone}` });
  }

  // Date de retrait
  const dateStr = new Date(data.pickupDate + 'T12:00:00').toLocaleDateString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  lines.push({ type: 'text', content: normalizeForPrint(`Retrait : ${dateStr}`) });
  lines.push({ type: 'separator' });

  // Articles
  for (const item of data.items) {
    const slicedLabel = item.sliced ? ' [COUPE]' : '';
    lines.push({ type: 'text', content: normalizeForPrint(`${item.product_name}${slicedLabel}`) });
    const total = ((item.quantity * item.price_cents) / 100).toFixed(2);
    lines.push({ type: 'text', content: `  ${item.quantity} x ${(item.price_cents / 100).toFixed(2)} = ${total} EUR` });
  }

  lines.push({ type: 'separator' });

  // Total
  lines.push({
    type: 'text',
    content: `TOTAL ESTIME = ${(data.totalCents / 100).toFixed(2)} EUR`,
    bold: true,
    fontSize: 'large',
  });

  // Notes
  if (data.notes) {
    lines.push({ type: 'separator' });
    lines.push({ type: 'text', content: normalizeForPrint(`Notes : ${data.notes}`) });
  }

  lines.push({ type: 'separator' });
  lines.push({ type: 'text', content: 'Paiement au retrait', align: 'center' });
  lines.push({ type: 'feed', feeds: 3 });
  lines.push({ type: 'cut' });

  return {
    lines,
    metadata: { saleNumber: data.preorderNumber, timestamp: new Date() },
  };
}

export default function PreorderCreateModal({ open, onClose, initialData, editPreorderId }: PreorderCreateModalProps) {
  const { tenant } = useTenant();

  // Client
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  // Consentement SMS, demandé au comptoir : c'est le seul moment où le client
  // a une raison de donner son numéro, et où l'accord est explicite.
  const [notifySms, setNotifySms] = useState(false);
  // Réassort magasin : ni échéance bloquante, ni alignement des dates.
  const [isStock, setIsStock] = useState(false);
  const [pickupDate, setPickupDate] = useState('');
  const [notes, setNotes] = useState('');

  // Produits
  const [preorderProducts, setPreorderProducts] = useState<PreorderProduct[]>([]);
  const [types, setTypes] = useState<any[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [items, setItems] = useState<SelectedItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  /**
   * Ce que ce client a DÉJÀ commandé (retrait à venir, pas encore remis).
   * Demande de Philippe (04/11/2026), à la reprise des commandes du boulanger :
   * ne pas commander deux fois la même chose. Nom exact OU téléphone, interrogé
   * après une courte pause de frappe ; sans réseau, rien ne s'affiche et la
   * saisie continue.
   */
  const [dejaCommande, setDejaCommande] = useState<PrecommandeEnCours[]>([]);
  useEffect(() => {
    if (!open || isStock) {
      setDejaCommande([]);
      return;
    }
    const nom = customerName.trim();
    const tel = customerPhone.replace(/\D/g, '');
    if (nom.length < 2 && tel.length < 8) {
      setDejaCommande([]);
      return;
    }
    let annule = false;
    const minuteur = setTimeout(async () => {
      try {
        const res = await api.get<PrecommandeEnCours[]>('/preorders/customer-open', {
          params: { name: nom, phone: customerPhone.trim(), exclude: editPreorderId || undefined },
        });
        if (!annule) setDejaCommande(Array.isArray(res) ? res : []);
      } catch {
        if (!annule) setDejaCommande([]);
      }
    }, 400);
    return () => {
      annule = true;
      clearTimeout(minuteur);
    };
  }, [open, isStock, customerName, customerPhone, editPreorderId]);
  const memeJour = pickupDate ? dejaCommande.filter((p) => p.pickup_date === pickupDate) : [];

  /**
   * Jours de retrait valides POUR LE PANIER, et non pour le catalogue entier.
   *
   * La date se choisit désormais APRÈS les produits : on connaît le type de
   * chaque ligne, donc on ne propose que les jours qui conviennent à TOUTES
   * (intersection). Avant, l'écran demandait la date en premier et affichait
   * l'union de tous les types — le jeudi du poisson et de la viande apparaissait
   * pour une commande de pain, que le serveur réalignait ensuite au vendredi.
   *
   * Priorité par ligne, identique à `computeDatesForType` côté serveur : les
   * jours du PRODUIT s'il en porte, sinon ceux de son TYPE, sinon aucune
   * contrainte — une ligne sans contrainte ne restreint personne.
   */
  const typeById = new Map<string, any>(types.map((t: any) => [t.id, t]));
  const productById = new Map<string, any>(preorderProducts.map((p: any) => [p.id, p]));
  const daysForItem = (productId: string): number[] | null => {
    const p = productById.get(productId);
    if (!p) return null;
    if (p.pickup_days?.length) return p.pickup_days as number[];
    const t = p.preorder_type_id ? typeById.get(p.preorder_type_id) : null;
    if (t?.pickup_days?.length) return t.pickup_days as number[];
    return null;
  };

  const unionTousTypes = Array.from(new Set(types.flatMap((t: any) => t.pickup_days || []))) as number[];
  const contraintes = items
    .map((i) => daysForItem(i.product_id))
    .filter((d): d is number[] => !!d && d.length > 0);
  // Panier dont aucune ligne ne contraint (ou panier vide) : on retombe sur le
  // comportement d'avant, l'union des types.
  const intersection = contraintes.length
    ? contraintes.reduce((acc, d) => acc.filter((x) => d.includes(x)))
    : unionTousTypes;
  // Aucun jour ne convient à tout le panier. Ne rien proposer bloquerait le
  // vendeur avec le client devant lui : on rouvre les jours et on prévient — la
  // commande est à scinder.
  const joursIncompatibles = contraintes.length > 0 && intersection.length === 0;
  const allowedWeekdays = joursIncompatibles
    ? Array.from(new Set(contraintes.flat()))
    : intersection;

  /**
   * Dates VÉRIFIÉES par le serveur (13/10/2026) : le même moteur que la
   * boutique en ligne (`POST /shop-config/pickup-dates`, canal « caisse »)
   * — délai de fabrication, jour de livraison du boulanger, heure limite,
   * congés du boulanger et fermetures du magasin. Avant, la caisse calculait
   * seule à partir des jours de la semaine, et le serveur reportait ensuite
   * la date choisie : le client repartait avec une date qui changeait.
   * Hors ligne (ou réassort magasin) : le calcul local d'avant, signalé.
   */
  const [datesServeur, setDatesServeur] = useState<string[] | null>(null);
  const [chargementDates, setChargementDates] = useState(false);
  const cleProduits = items.map((i) => i.product_id).sort().join(',');
  useEffect(() => {
    if (!open || isStock || items.length === 0) {
      // Une vérification en cours est abandonnée (cocher « stock magasin »
      // pendant qu'elle tourne) : sans cette remise à zéro, l'écran restait
      // sur « Vérification des dates… » et le bouton bloqué (05/11/2026).
      setDatesServeur(null);
      setChargementDates(false);
      return;
    }
    let annule = false;
    setChargementDates(true);
    api.post<{ dates?: string[] }>('/shop-config/pickup-dates', {
      product_ids: items.map((i) => i.product_id),
      canal: 'caisse',
    })
      .then((r: any) => { if (!annule) setDatesServeur(Array.isArray(r?.dates) ? r.dates : null); })
      .catch(() => { if (!annule) setDatesServeur(null); })
      .finally(() => { if (!annule) setChargementDates(false); });
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isStock, cleProduits]);
  const datesVerifiees = datesServeur !== null;
  const pickupDates = datesVerifiees ? datesServeur!.map(dateProposee) : buildPickupDates(allowedWeekdays);
  const [datesEtendues, setDatesEtendues] = useState(false);

  /**
   * La date retenue doit rester compatible avec le panier : changer un produit
   * peut invalider le jour déjà choisi. On ne recale QUE si le jour de semaine
   * est devenu impossible — une date valide mais lointaine (modification d'une
   * commande) doit survivre, sinon on écraserait la date promise au client.
   */
  // Dates vérifiées : une nouvelle précommande prend une date de la liste. Une
  // MODIFICATION garde sa date d'origine si son jour reste possible (la date
  // promise au client ne s'écrase pas en silence).
  const jourChoisiValide = pickupDate
    ? (datesVerifiees && !editPreorderId
      ? datesServeur!.includes(pickupDate)
      : allowedWeekdays.includes(new Date(pickupDate + 'T12:00:00').getDay()))
    : false;
  const cleJoursAutorises = allowedWeekdays.slice().sort().join(',');
  const cleDates = datesVerifiees ? datesServeur!.join(',') : 'local';
  useEffect(() => {
    if (items.length === 0 || chargementDates || jourChoisiValide) return;
    setPickupDate(pickupDates[0]?.value || '');
  }, [items.length > 0, cleJoursAutorises, cleDates, chargementDates]);
  // Une modification peut porter une date lointaine : la replier la rendrait
  // invisible, et le formulaire semblerait avoir perdu la date du client.
  const montrerToutesDates =
    datesEtendues || (!!pickupDate && !pickupDates.slice(0, 6).some((d) => d.value === pickupDate));
  const shownProducts = typeFilter === 'ALL' ? preorderProducts : preorderProducts.filter((p) => p.preorder_type_id === typeFilter);

  useEffect(() => {
    if (open) {
      setDatesEtendues(false);
      // Reconduction : client + lignes + notes repris ; la DATE reste neuve (on ne reconduit
      // pas un retrait passé). L'opérateur confirme le client, choisit une nouvelle date,
      // ajuste les quantités, puis valide → nouvelle précommande.
      setCustomerName(initialData?.customerName || '');
      setCustomerPhone(initialData?.customerPhone || '');
      setNotifySms(false);
      setIsStock(false);
      // La date se choisit une fois les produits connus : avant, on ne peut pas
      // savoir quels jours sont valides.
      setPickupDate(initialData?.pickupDate || '');
      // Seule la note LIBRE revient dans le champ : la mention « Pain coupe : … »
      // est recomposée à l'enregistrement depuis les articles (sinon elle s'empile).
      setNotes(noteLibre(initialData?.notes));
      setItems(initialData?.items || []);
      setSubmitting(false);
      loadProducts();
    }
  }, [open]);

  const loadProducts = async () => {
    setLoadingProducts(true);
    try {
      const result = await api.getProducts({ active: true, limit: 5000 });
      const products = (result.data || result || []) as any[];
      const filtered = products.filter((p: any) => p.preorder_enabled === true);
      setPreorderProducts(filtered);
      try {
        const t = await api.get('/preorders/types');
        setTypes(Array.isArray(t) ? t : []);
      } catch { /* types optionnels */ }
    } catch (error) {
      logger.error('[PreorderCreateModal] Erreur chargement produits:', error);
      toast.error('Erreur lors du chargement des produits');
    } finally {
      setLoadingProducts(false);
    }
  };

  const getItemQuantity = (productId: string): number => {
    return items.find((i) => i.product_id === productId)?.quantity || 0;
  };

  const updateQuantity = (product: PreorderProduct, delta: number) => {
    const existing = items.find((i) => i.product_id === product.id);
    const newQty = (existing?.quantity || 0) + delta;

    if (newQty <= 0) {
      setItems(items.filter((i) => i.product_id !== product.id));
    } else if (existing) {
      setItems(items.map((i) => (i.product_id === product.id ? { ...i, quantity: newQty } : i)));
    } else {
      setItems([
        ...items,
        {
          product_id: product.id,
          product_name: product.name,
          quantity: newQty,
          unit: product.unit || 'piece',
          price_cents: product.prices?.[0]?.amount_cents || 0,
          sliced: false,
        },
      ]);
    }
  };

  const toggleSliced = (productId: string) => {
    setItems(items.map((i) => (i.product_id === productId ? { ...i, sliced: !i.sliced } : i)));
  };

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalCents = items.reduce((sum, i) => sum + i.quantity * i.price_cents, 0);

  /** Construit les notes complètes incluant l'info "pain coupé" */
  const buildNotes = (): string | undefined => {
    const slicedItems = items.filter((i) => i.sliced);
    const parts: string[] = [];

    if (slicedItems.length > 0) {
      const slicedList = slicedItems.map((i) => i.product_name).join(', ');
      parts.push(`Pain coupe : ${slicedList}`);
    }

    if (notes.trim()) {
      parts.push(notes.trim());
    }

    return parts.length > 0 ? parts.join(' | ') : undefined;
  };

  /**
   * `effectivePickupDate` (YYYY-MM-DD) est la date RETENUE PAR LE SERVEUR, qui
   * n'est pas toujours celle demandée : il reporte au prochain jour possible
   * quand un article n'est pas disponible, pendant un congé du producteur ou
   * quand l'échéance de commande est passée. Le ticket était jusqu'ici construit
   * sur l'état local — le client repartait donc avec un papier annonçant un jour
   * où sa commande n'existait pas.
   */
  const printReceiptEpson = async (preorderNumber: string, effectivePickupDate: string) => {
    try {
      const bridgeAvailable = await PrinterAdapterFactory.checkHardwareBridge();
      if (!bridgeAvailable) {
        toast.warning('Imprimante non disponible');
        return;
      }

      const storeInfo = (tenant?.config as any)?.storeInfo || {};
      const printerConfig = (tenant?.config as any)?.hardware?.printer || {};

      const config: PrinterConfig = {
        model: printerConfig.type || 'BROWSER_PRINT',
        connection: 'USB',
        settings: printerConfig.settings,
      };

      const printer = await PrinterAdapterFactory.createAsync(config);
      await printer.connect();

      const receipt = generatePreorderReceipt({
        preorderNumber,
        customerName: customerName.trim(),
        customerPhone: customerPhone.trim() || undefined,
        pickupDate: effectivePickupDate,
        items,
        notes: buildNotes(),
        totalCents,
        storeName: storeInfo.name || tenant?.name || 'FOLIES BOUTIK',
      });

      await printer.print(receipt);
      await printer.disconnect();

      logger.log('[PreorderCreateModal] Ticket precommande imprime via EPSON');
      return true;
    } catch (error) {
      logger.error('[PreorderCreateModal] Erreur impression EPSON:', error);
      return false;
    }
  };

  /**
   * Alerte bloquante, pas un toast : le client est devant vous et attend une
   * date. Se tromper de jour lui fait faire le trajet pour rien.
   */
  const alerterReport = (dateRetenue: string) => {
    const jour = new Date(dateRetenue + 'T12:00:00').toLocaleDateString('fr-BE', {
      weekday: 'long', day: 'numeric', month: 'long',
    });
    window.alert(
      `Retrait reporte au ${jour}.\n\n`
      + 'Un des articles commandes n\'est pas disponible a la date demandee. '
      + 'La precommande a ete enregistree a cette nouvelle date — annoncez-la au client, '
      + 'le ticket porte cette date.',
    );
  };

  const handleSubmit = async () => {
    if (!tenant?.id || submitting) return;

    setSubmitting(true);
    try {
      const fullNotes = buildNotes();
      const itemsPayload = items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, prep_options: i.sliced ? ['coupé'] : [] }));

      // Mode ÉDITION : modifie la précommande existante (avant échéance)
      if (editPreorderId) {
        const modifiee: any = await api.patch(`/preorders/${editPreorderId}/modify`, {
          pickup_date: pickupDate,
          items: itemsPayload,
          notes: fullNotes,
        });

        // Le client repart avec son ticket : s'il ne reflète plus la commande,
        // il devient un faux. On réimprime donc apres modification, comme on
        // imprime a la creation. Le numero ne change pas — le nouveau ticket
        // remplace l'ancien, code-barres compris.
        const numero = modifiee?.preorder_number || '';
        // Le serveur peut reporter ici aussi (article indisponible, congé) :
        // prévenir AVANT d'imprimer, et imprimer la date réellement retenue.
        const dateModifiee = modifiee?.pickup_date
          ? new Date(modifiee.pickup_date).toISOString().slice(0, 10)
          : pickupDate;
        if (dateModifiee !== pickupDate) {
          alerterReport(dateModifiee);
        }
        const printed = numero ? await printReceiptEpson(numero, dateModifiee) : false;
        toast.success(
          printed
            ? `Precommande ${numero} modifiee + nouveau ticket imprime`
            : 'Precommande modifiee',
        );
        onClose();
        return;
      }

      // 1. Créer la précommande
      const preorder = await api.post('/preorders', {
        customer_name: customerName.trim(),
        customer_phone: customerPhone.trim() || undefined,
        // Sans numéro, le consentement n'a pas d'objet.
        notify_sms: notifySms && !!customerPhone.trim() && !isStock,
        is_stock: isStock,
        pickup_date: pickupDate,
        items: itemsPayload,
        notes: fullNotes,
        source: 'POS',
      });

      logger.log('[PreorderCreateModal] Precommande creee:', preorder.preorder_number);

      // Le serveur reporte la date quand un article n'est pas encore arrivé au
      // jour demandé (livraison plus tardive, échéance de commande dépassée).
      // Sans ce message, on annonçait au client un jour où sa commande n'aurait
      // pas été complète — la commande, elle, portait déjà la bonne date.
      const saved = preorder.pickup_date
        ? new Date(preorder.pickup_date).toISOString().slice(0, 10)
        : pickupDate;
      const wasShifted = saved !== pickupDate;

      // 2. Prévenir AVANT d'imprimer : le ticket qui sort doit porter la date
      //    que le vendeur vient d'annoncer, pas celle qui a été demandée.
      if (wasShifted) alerterReport(saved);

      // 3. Imprimer le ticket EPSON (non bloquant), à la date retenue
      const printed = await printReceiptEpson(preorder.preorder_number, saved);
      if (printed) {
        toast.success(`Precommande ${preorder.preorder_number} creee + ticket imprime`);
      } else {
        toast.success(`Precommande ${preorder.preorder_number} creee`);
      }

      onClose();
    } catch (error: any) {
      logger.error('[PreorderCreateModal] Erreur creation:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la creation de la precommande');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const peutCouper = (product: PreorderProduct | undefined): boolean => {
    if (!product) return false;
    // Une baguette, une ciabatta, une brioche ne se coupent pas (fiche) — même règle qu'en ligne.
    if ((product as any).ne_se_coupe_pas) return false;
    if (!product.preorder_type_id) return true;
    const type = types.find((t: any) => t.id === product.preorder_type_id);
    return ((type?.prep_options as string[] | undefined) || []).includes('coupé');
  };

  // Ce qui manque encore, dit sur le bouton lui-même : un bouton grisé sans
  // raison fait chercher le vendeur pendant que le client attend.
  const manque = !customerName.trim()
    ? 'Nom du client manquant'
    : items.length === 0
      ? 'Choisissez les produits'
      : chargementDates && !isStock
        ? 'Vérification des dates…'
        : !pickupDate
          ? 'Choisissez la date de retrait'
          : null;
  const canSubmit = !manque;

  const titreColonne = 'text-sm font-bold uppercase tracking-wide text-gray-500 mb-3';

  return (
    // Plein écran, une seule page. `data-clavier="page"` : le clavier à l'écran
    // resserre la page à côté de lui (voir index.css, `.page-plein-ecran`).
    <div
      data-clavier="page"
      className="page-plein-ecran @container fixed inset-0 z-50 bg-gray-50 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-precommande"
    >
      {/* En-tête */}
      <div className="bg-amber-500 px-5 py-2 flex items-center justify-between gap-4 flex-shrink-0">
        <h2 id="titre-precommande" className="text-xl font-bold text-white">
          {editPreorderId ? 'Modifier la précommande' : 'Nouvelle précommande'}
        </h2>
        <button
          onClick={onClose}
          className="min-h-[48px] px-5 rounded-lg bg-amber-600 text-white font-semibold text-base active:bg-amber-700"
        >
          ✕ Fermer
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto @5xl:overflow-hidden grid grid-cols-1 @5xl:grid-cols-[minmax(300px,22%)_1fr_minmax(360px,27%)] gap-4 p-4">
        {/* ── Client ─────────────────────────────────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 p-4 @5xl:min-h-0 @5xl:overflow-y-auto space-y-4">
          <h3 className={titreColonne}>Client</h3>
          <div>
            <label className="block text-base font-medium text-gray-700 mb-1">Nom du client *</label>
            <CustomerNameAutocomplete
              value={customerName}
              onChange={setCustomerName}
              onSelect={(c) => {
                // Le téléphone n'est repris que si le champ est vide : un
                // numéro déjà tapé est une intention, on ne l'écrase pas.
                if (c.phone && !customerPhone.trim()) setCustomerPhone(c.phone);
              }}
              autoFocus={!initialData?.customerName}
            />
          </div>

          <button
            type="button"
            onClick={() => {
              const next = !isStock;
              setIsStock(next);
              if (next) {
                setCustomerName('Stock magasin');
                setCustomerPhone('');
                setNotifySms(false);
              } else if (customerName === 'Stock magasin') {
                setCustomerName('');
              }
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left min-h-[52px] transition-colors ${
              isStock ? 'bg-blue-50 border-blue-400 text-blue-900' : 'bg-white border-gray-300 text-gray-700'
            }`}
          >
            <span className="text-2xl">{isStock ? '☑️' : '⬜'}</span>
            <span>
              <span className="font-semibold block">Commande stock magasin</span>
              <span className="text-sm opacity-80">Sans échéance ni report : chaque article arrive dès qu'il est cuit</span>
            </span>
          </button>

          <div>
            <label className="block text-base font-medium text-gray-700 mb-1">Téléphone</label>
            <input
              type="tel"
              value={customerPhone}
              onChange={(e) => setCustomerPhone(e.target.value)}
              placeholder="0470 12 34 56"
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200"
            />
            <button
              type="button"
              onClick={() => setNotifySms((v) => !v)}
              className={`mt-2 w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-left min-h-[52px] transition-colors ${
                notifySms ? 'bg-emerald-50 border-emerald-400 text-emerald-900' : 'bg-white border-gray-300 text-gray-700'
              }`}
            >
              <span className="text-2xl">{notifySms ? '☑️' : '⬜'}</span>
              <span>
                <span className="font-semibold block">Prévenir par SMS</span>
                <span className="text-sm opacity-80">Un message quand la commande est prête</span>
              </span>
            </button>
            {notifySms && !customerPhone.trim() && (
              <p className="mt-1 text-sm text-amber-700 font-medium">⚠️ Le téléphone est nécessaire pour envoyer le SMS.</p>
            )}
          </div>

          {dejaCommande.length > 0 && (
            <div className="rounded-lg border-2 border-orange-400 bg-orange-50 p-3 space-y-2" role="status">
              <p className="font-bold text-orange-900">
                ⚠️ Déjà commandé par ce client ({dejaCommande.length})
              </p>
              <ul className="space-y-2">
                {dejaCommande.map((p) => (
                  <li
                    key={p.id}
                    className={`rounded-md bg-white border p-2 text-sm ${
                      p.pickup_date === pickupDate ? 'border-red-500 ring-2 ring-red-200' : 'border-orange-200'
                    }`}
                  >
                    <div className="flex justify-between gap-2 font-semibold text-gray-900">
                      <span className="capitalize">
                        {new Date(p.pickup_date + 'T12:00:00').toLocaleDateString('fr-FR', {
                          weekday: 'long',
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                      <span className="text-gray-500 font-normal">{p.preorder_number}</span>
                    </div>
                    {p.customer_name.trim().toLowerCase() !== customerName.trim().toLowerCase() && (
                      <div className="text-xs text-gray-500">au nom de {p.customer_name}</div>
                    )}
                    <ul className="text-gray-700">
                      {p.items.map((it, i) => (
                        <li key={i}>
                          {it.quantity} × {it.product_name}
                          {it.prep_options?.length ? ` (${it.prep_options.join(', ')})` : ''}
                        </li>
                      ))}
                    </ul>
                    <div className="text-xs text-gray-500">
                      {p.source === 'ECOMMERCE' ? 'Commande en ligne' : 'Comptoir'}
                      {p.payment_status === 'PAID' ? ' · payée' : ''}
                      {p.status === 'READY' ? ' · prête' : ''}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <label className="block text-base font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions spéciales..."
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-4 py-3 text-base focus:outline-none focus:border-amber-500 focus:ring-1 focus:ring-amber-200 resize-none"
            />
          </div>
        </section>

        {/* ── Produits : tous visibles, un appui = +1 ─────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 p-4 @5xl:min-h-0 @5xl:overflow-y-auto">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className={`${titreColonne} mb-0`}>Produits — un appui ajoute 1</h3>
            {types.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {[{ id: 'ALL', name: 'Tous' }, ...types].map((t: any) => (
                  <button
                    key={t.id}
                    onClick={() => setTypeFilter(t.id)}
                    aria-pressed={typeFilter === t.id}
                    className={`min-h-[48px] px-4 rounded-full text-base border-2 ${
                      typeFilter === t.id ? 'border-amber-500 bg-amber-50 text-amber-800 font-semibold' : 'border-gray-200 text-gray-600'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          {loadingProducts ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin h-8 w-8 border-3 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : preorderProducts.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">Aucun produit disponible en précommande</p>
              <p className="text-base">Activez l'option « Précommande » sur les produits dans la fiche produit.</p>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-2">
              {shownProducts.map((product) => {
                const qty = getItemQuantity(product.id);
                const price = product.prices?.[0]?.amount_cents || 0;
                return (
                  <button
                    key={product.id}
                    onClick={() => updateQuantity(product, 1)}
                    className={`relative min-h-[92px] rounded-xl border-2 px-3 py-2 text-left flex flex-col justify-between transition-colors ${
                      qty > 0 ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white active:bg-amber-50'
                    }`}
                  >
                    <span className={`text-base font-medium text-gray-900 leading-tight line-clamp-3 ${qty > 0 ? 'pr-10' : ''}`}>
                      {product.name}
                    </span>
                    <span className="text-base font-bold text-amber-700 mt-1">{(price / 100).toFixed(2)} €</span>
                    {qty > 0 && (
                      <span className="absolute top-1.5 right-1.5 min-w-[36px] h-9 px-2 rounded-full bg-amber-500 text-white text-lg font-bold flex items-center justify-center">
                        {qty}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Commande, date, validation ─────────────────────── */}
        <section className="bg-white rounded-xl border border-gray-200 flex flex-col @5xl:min-h-0">
          <div className="p-4 @5xl:flex-1 @5xl:min-h-0 @5xl:overflow-y-auto space-y-4">
            <div>
              <h3 className={titreColonne}>Commande{totalItems > 0 ? ` (${totalItems})` : ''}</h3>
              {items.length === 0 ? (
                <p className="text-base text-gray-500">Touchez un produit pour l'ajouter.</p>
              ) : (
                <ul className="space-y-2">
                  {items.map((item) => {
                    const product = productById.get(item.product_id) as PreorderProduct | undefined;
                    const coupable = product ? peutCouper(product) : item.sliced;
                    return (
                      <li key={item.product_id} className="rounded-lg border border-gray-200 p-2">
                        <div className="flex justify-between gap-2">
                          <span className="text-base font-semibold text-gray-900 leading-tight">{item.product_name}</span>
                          <span className="text-base font-medium text-gray-700 whitespace-nowrap">
                            {((item.quantity * item.price_cents) / 100).toFixed(2)} €
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(product || { id: item.product_id, name: item.product_name, prices: [] }, -1)}
                            aria-label={`Retirer un ${item.product_name}`}
                            className="w-12 h-12 bg-gray-200 rounded-lg text-xl font-bold active:bg-gray-300 flex items-center justify-center"
                          >
                            −
                          </button>
                          <span className="text-xl font-bold text-gray-900 w-9 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(product || { id: item.product_id, name: item.product_name, prices: [] }, 1)}
                            aria-label={`Ajouter un ${item.product_name}`}
                            className="w-12 h-12 bg-amber-500 text-white rounded-lg text-xl font-bold active:bg-amber-600 flex items-center justify-center"
                          >
                            +
                          </button>
                          {coupable && (
                            <button
                              onClick={() => toggleSliced(item.product_id)}
                              aria-pressed={item.sliced}
                              className={`ml-auto min-h-[48px] px-3 rounded-lg text-base font-medium border-2 ${
                                item.sliced ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-300 text-gray-600'
                              }`}
                            >
                              {item.sliced ? '✂️ Coupé' : 'Non coupé'}
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Date de retrait — dès que les produits sont connus, pour ne
                proposer que les jours qui leur conviennent. */}
            <div>
              <h3 className={titreColonne}>Date de retrait *</h3>
              {items.length === 0 ? (
                <p className="text-base text-gray-500">Les dates possibles s'affichent avec les produits.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-500 mb-1">
                    {joursIncompatibles
                      ? "⚠️ Ces articles n'ont aucun jour de retrait commun — à scinder en deux précommandes."
                      : `Jours possibles : ${formatJours(allowedWeekdays)}`}
                  </p>
                  {!isStock && (
                    <p className={`text-sm mb-2 font-semibold ${datesVerifiees ? 'text-green-700' : 'text-amber-700'}`}>
                      {chargementDates
                        ? '⏳ Vérification des dates avec le boulanger…'
                        : datesVerifiees
                          ? pickupDates.length === 0
                            ? '⚠️ Aucune date possible dans les 12 semaines (congé du boulanger ou article indisponible).'
                            : '✓ Dates vérifiées (délais, heure limite, congés)'
                          : "⚠️ Hors ligne : dates non vérifiées — le serveur pourra reporter la date"}
                    </p>
                  )}
                  {/* Les trois prochaines semaines couvrent la quasi-totalité des
                      commandes ; le reste est à un appui. */}
                  <div className="grid grid-cols-2 gap-2">
                    {(montrerToutesDates ? pickupDates : pickupDates.slice(0, 6)).map((d) => (
                      <button
                        key={d.value}
                        onClick={() => setPickupDate(d.value)}
                        aria-pressed={pickupDate === d.value}
                        className={`min-h-[48px] px-3 rounded-lg text-base font-medium border-2 transition-colors ${
                          pickupDate === d.value
                            ? 'border-amber-500 bg-amber-50 text-amber-800'
                            : 'border-gray-200 text-gray-700 active:bg-gray-50'
                        }`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                  {!montrerToutesDates && pickupDates.length > 6 && (
                    <button
                      onClick={() => setDatesEtendues(true)}
                      className="mt-2 w-full min-h-[48px] rounded-lg text-base font-semibold text-amber-800 border-2 border-dashed border-amber-300 active:bg-amber-50"
                    >
                      📅 Dates plus lointaines
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Validation, toujours visible */}
          <div className="border-t border-gray-200 p-4 flex-shrink-0 space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="text-base font-bold text-gray-900">Total estimé</span>
              <span className="text-2xl font-bold text-amber-700">{(totalCents / 100).toFixed(2)} €</span>
            </div>
            {pickupDate && items.length > 0 && (
              <p className="text-base text-gray-700">
                Retrait le{' '}
                <strong>
                  {new Date(pickupDate + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                </strong>{' '}
                · paiement au retrait
              </p>
            )}
            {memeJour.length > 0 && (
              <p className="text-base font-semibold text-red-700 bg-red-50 border border-red-300 rounded-lg px-3 py-2">
                ⚠️ Ce client a déjà {memeJour.length > 1 ? `${memeJour.length} précommandes` : 'une précommande'} pour
                ce jour ({memeJour.map((p) => p.preorder_number).join(', ')}). Vérifiez avant de confirmer.
              </p>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              className="w-full min-h-[56px] bg-green-600 text-white rounded-lg text-lg font-bold active:bg-green-700 disabled:bg-gray-300 disabled:text-gray-600 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Enregistrement…
                </>
              ) : (
                manque || (editPreorderId ? 'Enregistrer + ticket' : 'Confirmer + ticket')
              )}
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
